import React from 'react';
import { useEntityBands } from './hooks';
import { orderByFamily } from './metricMeta';
import { bandLevels, describeCell, sumCounts } from './intensity';
import { emptyRowAttr } from './rowFilters';
import { Bucket, ReportQuery } from './types';
import { BandLabelCell, BandTotalCell, HeatTd } from './Accountability.style';
import HeatCell from './HeatCell';

interface MetricBandsProps {
  query: ReportQuery;
  entityKey: string;
  entityName: string;
  bucket: Bucket;
  bucketLabels: string[];
  depth: number;
  /**
   * Admin override for the active-day denominator. Metric bands describe
   * raw event counts, not active days, so this does not change their level
   * or copy today -- it is threaded through purely so every `describeCell`
   * call site is consistent, and so it is not lost if a band ever grows an
   * active-day-relative description.
   */
  activeDayTarget?: number | null;
  /** Currently hovered column index (from AccountabilityGrid), or null. */
  hoveredCol: number | null;
  /** Activity types nobody in the whole view used. A row's own zeros never hide it. */
  hiddenMetricSlugs: ReadonlySet<string>;
}

/**
 * The per-activity strips for one expanded entity, each with its window total.
 * Each band is scaled to its own range (see intensity.bandLevels), because
 * slide views and lessons completed have incomparable magnitudes.
 */
const MetricBands: React.FC<MetricBandsProps> = ({
  query,
  entityKey,
  entityName,
  bucket,
  bucketLabels,
  depth,
  activeDayTarget,
  hoveredCol,
  hiddenMetricSlugs,
}) => {
  const { data, isLoading, error } = useEntityBands(query, entityKey);
  // One cell for the Total column, then one per bucket.
  const valueColumns = bucketLabels.length + 1;

  if (isLoading) {
    return (
      <tr>
        <BandLabelCell depth={depth}>Loading metrics…</BandLabelCell>
        <HeatTd colSpan={valueColumns} />
      </tr>
    );
  }

  if (error || !data) {
    return (
      <tr>
        <BandLabelCell depth={depth}>Could not load metrics.</BandLabelCell>
        <HeatTd colSpan={valueColumns} />
      </tr>
    );
  }

  const bands = orderByFamily(data.bands).filter(
    (band) => !hiddenMetricSlugs.has(band.slug),
  );

  if (bands.length === 0) {
    return (
      <tr>
        <BandLabelCell depth={depth}>No activity types used in this view.</BandLabelCell>
        <HeatTd colSpan={valueColumns} />
      </tr>
    );
  }

  return (
    <>
      {bands.map((band) => {
        const levels = bandLevels(band.counts);
        return (
          <tr key={band.slug} data-empty={emptyRowAttr(band.counts)}>
            <BandLabelCell depth={depth} data-testid="band-label">
              {band.label}
            </BandLabelCell>
            <BandTotalCell data-testid="band-total">
              {sumCounts(band.counts)}
            </BandTotalCell>
            {band.counts.map((count, index) => (
              <HeatCell
                key={`${band.slug}-${index}`}
                thin
                colIndex={index}
                highlighted={hoveredCol === index}
                count={count}
                level={levels[index]}
                label={describeCell({
                  entityName,
                  bucketLabel: bucketLabels[index],
                  count,
                  kind: 'metric',
                  metricLabel: band.label,
                  bucket,
                  activeDayTarget,
                })}
              />
            ))}
          </tr>
        );
      })}
    </>
  );
};

export default MetricBands;
